<script setup>
import { ref, computed, provide } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { useResultsCardVisibility } from "@/composables/useResultsCardVisibility";
import ResultsV2QuestionCard from "./ResultsV2QuestionCard.vue";
import ResultsV2RecodeCard from "./ResultsV2RecodeCard.vue";
import ResultsV2TextCard from "./ResultsV2TextCard.vue";
import ResultsV2SectionCard from "./ResultsV2SectionCard.vue";
import ResultsV2AnalysisCard from "./ResultsV2AnalysisCard.vue";
import ResultsV2ImageCard from "./ResultsV2ImageCard.vue";
import ResultsV2KpiCard from "./ResultsV2KpiCard.vue";
import ResultsV2KpiTrendCard from "./ResultsV2KpiTrendCard.vue";
import ResultsV2CrosstabCard from "./ResultsV2CrosstabCard.vue";
import ResultsV2FunnelCard from "./ResultsV2FunnelCard.vue";
import ResultsV2SankeyCard from "./ResultsV2SankeyCard.vue";
import ResultsV2OverlapCard from "./ResultsV2OverlapCard.vue";
import ResultsV2GeoMapCard from "./ResultsV2GeoMapCard.vue";
import ResultsV2WordCloudCard from "./ResultsV2WordCloudCard.vue";
import ResultsV2KeyDriversCard from "./ResultsV2KeyDriversCard.vue";
import ResultsV2TurfCard from "./ResultsV2TurfCard.vue";
import { LoadingState } from "@/components/episto-ui/loading-state";
import { cardSubtype, contentSubtype } from "../constants";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const store = useResultsV2Store();
const cardsStore = useResultsV2CardsStore();
const { editMode, printing, printTallCardIds } = storeToRefs(store);
const { selectedCard } = storeToRefs(cardsStore);

const cardRef = ref(null);
const { isVisible, isRevealed } = useResultsCardVisibility(cardRef, printing);

const animateEntrance = computed(() => !editMode.value && !printing.value);
provide(
  "resultsCardRevealed",
  computed(() => !animateEntrance.value || isRevealed.value),
);

const isSelected = computed(() => selectedCard.value?.id === props.card.id);

const { availableCards } = storeToRefs(store);

const componentMap = {
  question: ResultsV2QuestionCard,
  recode: ResultsV2RecodeCard,
};

const CONTENT_COMPONENTS = {
  section: ResultsV2SectionCard,
  text: ResultsV2TextCard,
  image: ResultsV2ImageCard,
};

const ANALYSIS_COMPONENTS = {
  crosstab: ResultsV2CrosstabCard,
  funnel: ResultsV2FunnelCard,
  sankey: ResultsV2SankeyCard,
  overlap: ResultsV2OverlapCard,
  geo_map: ResultsV2GeoMapCard,
  word_cloud: ResultsV2WordCloudCard,
  importance_performance: ResultsV2AnalysisCard,
  key_drivers: ResultsV2KeyDriversCard,
  turf: ResultsV2TurfCard,
  kpi: ResultsV2KpiCard,
  kpi_trend: ResultsV2KpiTrendCard,
};

const cardComponent = computed(() => {
  // Recode-from-open cards should use the question card component (with verbatim interface)
  if (props.card.card_type === "recode") {
    const meta = availableCards.value.find((c) => c.id === props.card.card_id);
    if (meta?.is_recode_question) return ResultsV2QuestionCard;
  }
  // "content" and "analysis" are families dispatched by their content.sub_type.
  const contentKind = contentSubtype(props.card);
  if (contentKind) return CONTENT_COMPONENTS[contentKind] || null;
  if (props.card.card_type === "analysis") return ANALYSIS_COMPONENTS[cardSubtype(props.card)] || null;
  return componentMap[props.card.card_type] || null;
});
const hasCardBackground = computed(() =>
  ["question", "recode", "analysis"].includes(props.card.card_type),
);

// In view mode, section headers stick below the top of the scroll pane while their
// section is in view. -top-6 hides most of the section's pt-8 breathing room once stuck.
const isStickySection = computed(
  () => contentSubtype(props.card) === "section" && !editMode.value,
);

function handleClick() {
  if (editMode.value && !props.readonly) {
    cardsStore.selectCard(props.card.id);
  }
}
</script>

<template>
  <div
    :id="`results-card-${card.id}`"
    ref="cardRef"
    data-results-card
    class="transition-all"
    :class="{
      'print:break-inside-avoid': !printTallCardIds.has(card.id),
      'rounded-xl border bg-card': hasCardBackground,
      'shadow-xs': hasCardBackground && !editMode,
      'rounded-xl p-0.5': editMode && !hasCardBackground,
      'ring-1 ring-inset ring-ring': isSelected,
      'cursor-pointer hover:shadow-sm': editMode && !readonly,
      'duration-500 ease-out': animateEntrance,
      'opacity-0 translate-y-6': animateEntrance && !isRevealed,
      'sticky -top-6 z-10 bg-page/95 backdrop-blur-sm print:static print:bg-transparent print:backdrop-blur-none':
        isStickySection,
    }"
    @click="handleClick">
    <template v-if="isVisible && cardComponent">
      <component :is="cardComponent" :card="card" :readonly="readonly" />
    </template>
    <template v-else-if="!isVisible">
      <LoadingState size="sm" class="h-48" />
    </template>
  </div>
</template>
